import Vue from 'vue'
import Vuex, {ActionContext} from 'vuex'
import axios from "axios";
import gorevYoneticisi from "@/plugins/uyap-plugin/store/modules/GorevYoneticisi";
import {AppState, AppStatus, ITenant, ManifestResponse, UyapGirisAsamasi, UyapStatusMessage} from '@/types';
import VueSocketIOExt from 'vue-socket.io-extended';
import {io} from 'socket.io-client';
import app from "@/main";
import {KullaniciBilgileriCevap} from "@/plugins/uyap-plugin/uyap/KullaniciBilgileri";
import {
    oturumKimligiUyusuyor,
    hesabaAitKartiSec,
    tcknNormalize,
    kimlikUyusmazligiKancasiniAyarla,
    KIMLIK_UYUSMAZLIGI_MESAJI
} from "@/store/oturumKimligi";
import {isletimSistemiBildirimi, uyariSesi} from "@/plugins/kritikUyari";
import {
    kotaUyarisiKancasiniAyarla,
    devreKesiciKancasiniAyarla,
    durumMesajiKancasiniAyarla,
    devreKesiciAcikMi,
    devreKesiciSifirla,
    DurumMesajiTipi
} from "@/plugins/uyap-plugin/BaglantiKurtarma";
import {PlayerDurum} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";
import {EICRAPRO_BEKLENIYOR_MESAJI, eicraproKimligiVarMi} from "@/utils/eicraproBaglantisi";

const socket = io('https://imza.eicrapro.com:3000');
Vue.use(VueSocketIOExt, socket);
Vue.use(Vuex);

// connectToUyap için in-flight kilidi: eşzamanlı çağrılar (Bağlan butonu, socket
// eventi, health check) aynı denemeyi paylaşır; geç biten eski bir deneme yeni
// kurulmuş bağlantıyı false'a ezemez.
let connectToUyapPromise: Promise<void> | null = null;

// Ardışık başarısızlıkta tik atlayarak geri çekilme için.
let healthTikSayaci = 0;

export const HEALTH_CHECK_ARALIK_MS = 30000;

// Kart gözlemcisi. Yoklama yerel imzalama uygulamasına gider (127.0.0.1:5975),
// UYAP'a değil; sıklık ban riski taşımaz. Kart takılıyken sık sormanın faydası
// olmadığı için periyot uzar. Tik sabit, periyot tik atlanarak uygulanır.
export const KART_YOKLAMA_TIK_MS = 1000;
export const KART_YOKLAMA_KART_YOK_MS = 3000;
// Kart takılıyken uzun: bu uç kart kullanıldıktan sonra ~4 sn sürüyor ve imzalama
// ile aynı token'ı meşgul ediyor. Buradaki tek amaç kart çıkarılmasını görmek.
export const KART_YOKLAMA_KART_VAR_MS = 60000;

let kartGozlemciTimer: number | null = null;
let sonKartYoklamasi = 0;

// Otomatik giriş geri çekilmesi. Kurtarmanınkinden (3/15/30 sn) BİLEREK daha
// uzun başlar: kurtarma bir görevin ortasında kullanıcı beklerken çalışır,
// otomatik giriş ise arka planda kimse beklemezken. Login spam bir kez IP
// yasağına yol açtı; arka plandaki bir denemenin acelesi yok.
export const OTO_GIRIS_BEKLEMELERI_MS = [30_000, 60_000, 120_000, 300_000];
// Art arda bu kadar başarısızlıktan sonra otomatik giriş susar. Yeniden
// başlaması için ya kart çıkarılıp takılmalı ya da kullanıcı "Bağlan"a basmalı.
export const OTO_GIRIS_MAX_DENEME = 5;

let otoGirisBasarisizSayisi = 0;
let otoGirisSonrakiDenemeZamani = 0;

function otoGirisBeklemesiMs(deneme: number): number {
    const i = Math.min(Math.max(deneme - 1, 0), OTO_GIRIS_BEKLEMELERI_MS.length - 1);
    return OTO_GIRIS_BEKLEMELERI_MS[i];
}

function oturumDeposu(): Storage | null {
    try {
        return window.sessionStorage;
    } catch (e) {
        return null;
    }
}

/**
 * eİcraPro kimliği imzacıya devredildi mi. Bayrak her soruşta sessionStorage'dan
 * yeniden türetilir: kaçan bir `updateToken` olayı ya da 401 sonrası silinen
 * token ilk denemede kendini düzeltir. Ayrıntı: utils/eicraproBaglantisi.ts.
 */
function eicraproBagli(ctx: ActionContext<AppState, AppState>): boolean {
    ctx.commit('tokenDurumunuYenile');
    return ctx.state.tokenExis;
}

// Servis hazırlık yoklaması: sabit uyku yerine yoklayarak bekle.
export const SERVIS_YOKLAMA_ARALIK_MS = 200;
export const SERVIS_YOKLAMA_TAVAN_MS = 6000;

/**
 * yokla() true dönene kadar bekler; tavana ulaşınca yine de devam eder.
 * Servis kalkmadıysa hata fırlatmaz: çağıran akış (load) zaten servis kapalıyken
 * de çalışabiliyor, kullanıcıya durumu ana ekrandaki servis kartı gösteriyor.
 */
async function servisiBekle(yokla: () => Promise<boolean>): Promise<boolean> {
    const bitis = Date.now() + SERVIS_YOKLAMA_TAVAN_MS;
    while (Date.now() < bitis) {
        try {
            if (await yokla()) {
                return true;
            }
        } catch (e) {
            /* yoklama hatası = henüz hazır değil */
        }
        await new Promise((res) => setTimeout(res, SERVIS_YOKLAMA_ARALIK_MS));
    }
    return false;
}

export const store = new Vuex.Store<AppState>({
    modules: {
        // @ts-ignore
        gorevYoneticisi
    },
    state: {
        uyapConnection: false,
        appStatus: AppStatus.loading,
        tenant: undefined,
        manifestResponse: {files: []},
        availableCardList: [],
        selectedCardSerial: null,
        tokenExis: false,
        devMode: true,
        eicraproDevMode: false,  // Mock data kullanmak için true yap
        uyapAvukatBilgileri: undefined,
        uyapAvukatBakiye: undefined,
        uyapSorguBakiye: undefined,
        uyapStatusMessage: null,
        connectionHealthCheckInterval: null as number | null,
        healthCheckRetryCount: 0,
        healthCheckMaxRetries: 3,
        // Health check ile durum kartının paylaştığı son yoklama sonucu.
        sonImzaProbe: null as { basarili: boolean, zaman: number } | null,
        // UYAP 5 oturum sınırına takıldığında gösterilen uyarı (null = kapalı).
        oturumLimitiUyarisi: null as { zaman: number } | null,
        // 401 devre kesici açıldığında gösterilen uyarı (null = kapalı).
        devreKesiciUyarisi: null as { zaman: number } | null,
        // Giriş aşaması; null = giriş yapılmıyor.
        uyapGirisAsamasi: null as UyapGirisAsamasi | null,
        // Açık UYAP oturumu hesaba ait değil (null = sorun yok).
        kimlikUyusmazligiUyarisi: null as { zaman: number, sebep: 'hesap' | 'kart' } | null
    },
    getters: {
        appStatus: (state) => state.appStatus,
        isLoading: (state) => state.appStatus === AppStatus.loading,
        isUpdate: (state) => state.appStatus === AppStatus.update,
        isBrowser: (state) => state.appStatus === AppStatus.browser,
        isElectron: (state) => state.appStatus === AppStatus.electron,
        isUyapConnected: (state) => state.uyapConnection,
        isTokenExis: (state) => state.tokenExis,
        // Ortak plugin'in bot döngüsü bu adı okur (GorevYoneticisi.ts taskInterval);
        // icrapro-app'te tanımsızdır ve orada kapı açık sayılır.
        isEicraproBagli: (state) => state.tokenExis,
        isDevMode: (state) => state.devMode,
        isEicraproDevMode: (state) => state.eicraproDevMode,
        uyapAvukatBilgileri: (state) => state.uyapAvukatBilgileri,
        uyapAvukatBakiye: (state) => state.uyapAvukatBakiye,
        uyapSorguBakiye: (state) => state.uyapSorguBakiye,
        availableCardList: (state) => state.availableCardList,
        selectedCardSerial: (state) => state.selectedCardSerial,
        tenantBilgileri: (state) => state.tenant,
        uyapStatusMessage: (state) => state.uyapStatusMessage,
        healthCheckRetryCount: (state) => state.healthCheckRetryCount,
        isHealthChecking: (state) => state.connectionHealthCheckInterval !== null,
        sonImzaProbe: (state) => state.sonImzaProbe,
        oturumLimitiUyarisi: (state) => state.oturumLimitiUyarisi,
        devreKesiciUyarisi: (state) => state.devreKesiciUyarisi,
        uyapGirisAsamasi: (state) => state.uyapGirisAsamasi,
        isUyapConnecting: (state) => state.uyapGirisAsamasi !== null,
        kimlikUyusmazligiUyarisi: (state) => state.kimlikUyusmazligiUyarisi
    },
    mutations: {
        setAppState(state: AppState, value: AppStatus): void {
            state.appStatus = value;
        },
        setManifestResponse(state: AppState, response: ManifestResponse) {
            state.manifestResponse = response;
        },
        setAvailableCardList(state: AppState, value: any) {
            state.availableCardList = value;
        },
        setSelectedCardSerial(state: AppState, serial: string | null) {
            state.selectedCardSerial = serial;
        },
        setUyapConnection(state: AppState, payload: boolean) {
            app.$socket.client.emit("uyapconnection", {
                connected: payload,
                JSESSIONID: payload ? sessionStorage.getItem('JSESSIONID') : ''
            });
            state.uyapConnection = payload;
        },
        setUyapAvukatBilgileri(state: AppState, avukat: KullaniciBilgileriCevap | undefined) {
            state.uyapAvukatBilgileri = avukat;
        },
        setUyapAvukatBakiye(state: AppState, payload: string | undefined) {
            state.uyapAvukatBakiye = payload;
        },
        setUyapSorguBakiye(state: AppState, payload: string | undefined) {
            state.uyapSorguBakiye = payload;
        },
        setTenant(state: AppState, tenant: ITenant) {
            state.tenant = tenant;
        },
        setUyapStatusMessage(state: AppState, payload: UyapStatusMessage | null) {
            state.uyapStatusMessage = payload;
        },
        setHealthCheckInterval(state: AppState, interval: any) {
            state.connectionHealthCheckInterval = interval;
        },
        setHealthCheckRetryCount(state: AppState, count: number) {
            state.healthCheckRetryCount = count;
        },
        setSonImzaProbe(state: AppState, payload: { basarili: boolean, zaman: number }) {
            state.sonImzaProbe = payload;
        },
        setOturumLimitiUyarisi(state: AppState, payload: { zaman: number } | null) {
            state.oturumLimitiUyarisi = payload;
        },
        setDevreKesiciUyarisi(state: AppState, payload: { zaman: number } | null) {
            state.devreKesiciUyarisi = payload;
        },
        setUyapGirisAsamasi(state: AppState, payload: UyapGirisAsamasi | null) {
            state.uyapGirisAsamasi = payload;
        },
        setKimlikUyusmazligiUyarisi(state: AppState, payload: { zaman: number, sebep: 'hesap' | 'kart' } | null) {
            state.kimlikUyusmazligiUyarisi = payload;
        },
        // tokenExis'in TEK yazıcısı. Eskiden üç yer doğrudan yazıyordu (load,
        // updateToken, axios 401) ve updateToken token'a bakmadan true yapıyordu.
        tokenDurumunuYenile(state: AppState) {
            state.tokenExis = eicraproKimligiVarMi(oturumDeposu());
        },
    },
    actions: {
        /**
         * UYAP'a bağlan.
         *
         * @param payload.force  true ise mevcut oturum geçerli olsa bile yeniden giriş
         *                       yapılır. Kullanıcının açık talebi (Bağlan düğmesi) ve
         *                       kart değişimi için gerekli — aksi halde eski kartın
         *                       oturumu geçerli bulunup yanlış avukatla devam edilir.
         */
        async connectToUyap(
            ctx: ActionContext<AppState, AppState>,
            payload?: { force?: boolean, sessiz?: boolean }
        ) {
            // eİcraPro ile bağlantı sağlanmadan UYAP'a GİRİLMEZ (DEV-2603): hesap
            // TCKN'si henüz bilinmediği için kimlik denetimi atlanır, oturum kime ait
            // olduğu bilinmeden açılır. Bütün giriş yolları (kart takılma, sağlık tiki,
            // Bağlan düğmesi, dialog'lar, socket connectToUyap, bot döngüsünün yeniden
            // bağlanması) bu huniden geçer. Kilidin önünde ve ona dokunmadan: süren bir
            // denemeyi bozmaz. Kimlik gelince giriş updateToken'da yeniden denenir.
            if (!eicraproBagli(ctx)) {
                throw new Error(EICRAPRO_BEKLENIYOR_MESAJI);
            }
            if (connectToUyapPromise) {
                return connectToUyapPromise;
            }
            connectToUyapPromise = (async () => {
                // Aşama bilgisi durum kartındaki "Bağlanıyor" ekranını sürüyor.
                // İmzalama adımı tek başına ~5 sn; aşama yayınlanmazsa kullanıcı
                // ekranın donduğunu sanıyor ve tekrar tıklıyor.
                ctx.commit('setUyapGirisAsamasi', 'oturum-kontrol');
                try{
                    // Her betaLogin UYAP'ta YENİ oturum açar ama eskisini kapatmaz
                    // (clearCookies yalnızca yerel cookie'yi siler, UYAP'ta logout ucu yok).
                    // 5 oturum kotası dolunca PRTL_GNL_100026-1 gelir. Bu yüzden oturum
                    // hâlâ geçerliyse giriş yapılmaz. KullaniciBilgileri önbelleklenmiyor
                    // (cache active:false), yani bu gerçek bir oturum testidir.
                    if (!payload?.force && await ctx.dispatch('uyapOturumuGecerliMi')) {
                        ctx.commit('setUyapConnection', true);
                        devreKesiciSifirla();
                        ctx.commit('setUyapGirisAsamasi', 'bilgiler');
                        await ctx.dispatch('loadUyapMetaData').catch((e) => console.error('UYAP meta verisi yüklenemedi:', e));
                        return;
                    }
                    ctx.commit('setUyapGirisAsamasi', 'imza');
                    await app.$imza.betaLogin(payload?.sessiz === true);
                    ctx.commit('setUyapGirisAsamasi', 'bilgiler');
                    await ctx.dispatch('loadUyapMetaData');
                    ctx.commit('setUyapConnection', true);
                    // Oturum gerçekten açıldı: 401 serisi bitti, kesici kapanır.
                    // Aksi halde kesici bir kez açıldığında kullanıcı elle bağlansa
                    // bile otomatik giriş kalıcı olarak susuyordu (devreKesiciSifirla
                    // yalnızca bot kurtarması ve dialogdaki "Yeniden Başlat" yolunda
                    // çağrılıyor; dialogu kapatıp elle bağlanan kullanıcı o yollara girmiyor).
                    devreKesiciSifirla();
                    ctx.commit('setUyapStatusMessage', {
                        type: 'success',
                        text: 'Uyap bağlantısı tamamlandı.',
                        timestamp: Date.now()
                    });
                    //app.$toast.success('Uyap bağlantısı tamamlandı.');
                }catch (error) {
                    ctx.commit('setUyapConnection', false);
                    const errMsg = (error as any)?.message ?? String(error);
                    const message = `Uyap bağlantısı yapılamadı. (${errMsg})`;
                    ctx.commit('setUyapStatusMessage', {
                        type: 'error',
                        text: message,
                        timestamp: Date.now()
                    });
                    throw error instanceof Error ? error : new Error(errMsg);
                } finally {
                    ctx.commit('setUyapGirisAsamasi', null);
                    connectToUyapPromise = null;
                }
            })();
            return connectToUyapPromise;
        },
        /**
         * UYAP 5 oturum sınırı uyarısı. Dialog + işletim sistemi bildirimi + ses.
         * Ses yalnızca burada çalar; her uyarıda çalarsa sinyal değerini yitirir.
         */
        oturumLimitiUyarisiGoster(ctx: ActionContext<AppState, AppState>) {
            // Uyarı zaten açıksa tekrar bildirim/ses üretme.
            if (ctx.state.oturumLimitiUyarisi) {
                return;
            }
            ctx.commit('setOturumLimitiUyarisi', {zaman: Date.now()});
            ctx.commit('setUyapStatusMessage', {
                type: 'error',
                text: 'UYAP oturum sınırına ulaşıldı. Açık UYAP oturumlarınızı kapatıp yeniden bağlanın.',
                timestamp: Date.now()
            });
            isletimSistemiBildirimi(
                'UYAP oturum sınırına ulaşıldı',
                'Açık UYAP oturumlarınızı kapatıp yeniden bağlanın.'
            );
            uyariSesi();
        },
        oturumLimitiUyarisiniKapat(ctx: ActionContext<AppState, AppState>) {
            ctx.commit('setOturumLimitiUyarisi', null);
        },
        /**
         * 401 devre kesici açıldı: kısa sürede çok sayıda oturum hatası alındı ve
         * denemeye devam etmek ban riski taşıyor. Bot durdurulur, kullanıcıya
         * dialog + bildirim + ses verilir. Kesici yalnızca kullanıcı bilinçli
         * olarak yeniden başlattığında sıfırlanır.
         */
        devreKesiciUyarisiGoster(ctx: ActionContext<AppState, AppState>) {
            if (ctx.state.devreKesiciUyarisi) {
                return;
            }
            ctx.commit('setPlayerDurum', PlayerDurum.dur);
            ctx.commit('setDevreKesiciUyarisi', {zaman: Date.now()});
            ctx.commit('setUyapStatusMessage', {
                type: 'error',
                text: 'UYAP bağlantısında tekrarlayan hata; ban riskine karşı işlemler durduruldu.',
                timestamp: Date.now()
            });
            isletimSistemiBildirimi(
                'İşlemler durduruldu',
                'UYAP bağlantısında tekrarlayan hata; ban riskine karşı işlemler durduruldu.'
            );
            uyariSesi();
        },
        devreKesiciUyarisiniKapat(ctx: ActionContext<AppState, AppState>) {
            ctx.commit('setDevreKesiciUyarisi', null);
        },
        /**
         * Açık UYAP oturumu bu hesaba ait değil. GÜVENLİK OLAYI, engelleyici.
         *
         * Bot durdurulur, bağlantı bayrağı düşürülür, engelleyici dialog + işletim
         * sistemi bildirimi + ses verilir. Uyarı kullanıcı kartı/hesabı düzeltip
         * yeniden bağlanana kadar kalır; kendiliğinden kapanmaz.
         */
        kimlikUyusmazligiUyarisiGoster(
            ctx: ActionContext<AppState, AppState>,
            payload?: { sebep?: 'hesap' | 'kart' }
        ) {
            if (ctx.state.kimlikUyusmazligiUyarisi) {
                return;
            }
            ctx.commit('setPlayerDurum', PlayerDurum.dur);
            ctx.commit('setUyapConnection', false);
            ctx.commit('setKimlikUyusmazligiUyarisi', {
                zaman: Date.now(),
                sebep: payload?.sebep ?? 'hesap'
            });
            ctx.commit('setUyapStatusMessage', {
                type: 'error',
                text: KIMLIK_UYUSMAZLIGI_MESAJI,
                timestamp: Date.now()
            });
            isletimSistemiBildirimi(
                'UYAP oturumu bu hesaba ait değil',
                'İşlemler durduruldu. Kendi hesabınızla giriş yapıp kendi e-imzanızı kullanın.'
            );
            uyariSesi();
        },
        kimlikUyusmazligiUyarisiniKapat(ctx: ActionContext<AppState, AppState>) {
            ctx.commit('setKimlikUyusmazligiUyarisi', null);
        },
        /**
         * Oturum gerçekten ayakta mı. KullaniciBilgileri önbelleksiz olduğu için canlı testtir.
         *
         * probe() ŞART: bu bir yoklama, iş isteği değil. probe()'suz çağrıldığında
         * UyapRequest 401'i "oturum düştü" sayıp kurtarma turlarına giriyor (3+15+30 sn)
         * ve o kurtarma connectToUyap yayınlıyor — yani burayı çağıran connectToUyap'ın
         * kendisini bekliyor. Sonuç: her "Bağlan" tıklaması 48 sn boşa bekliyordu.
         */
        async uyapOturumuGecerliMi(ctx: ActionContext<AppState, AppState>): Promise<boolean> {
            let avukat: KullaniciBilgileriCevap;
            try {
                avukat = await app.$uyap.KullaniciBilgileri().probe().run({});
            } catch (e) {
                return false;
            }

            // Oturum "geçerli" olsa bile YANLIŞ KİMLİĞE ait olabilir.
            // Hesap TC'si sessionStorage'a Electron proxy tarafından yazılır
            // (setConfig; accessToken ile aynı istekte, yani aynı hesaptan).
            //
            // Denetimden ÖNCE yazılıyor: UyapRequest'teki kimlik kapısı oturumun
            // kime ait olduğunu buradan okuyor. Uyuşmazlıkta yazılmasaydı kapı
            // "bilinmiyor" deyip her isteği geçirirdi.
            ctx.commit('setUyapAvukatBilgileri', avukat);

            const secili = ctx.state.availableCardList?.find(
                (c: any) => c?.certificate?.certificateId === ctx.state.selectedCardSerial
            );
            const denetim = oturumKimligiUyusuyor({
                oturum: avukat?.tcKimlikNo,
                hesap: sessionStorage.getItem('tckn'),
                kart: secili?.certificate?.subjectSerial
            });
            if (!denetim.uyusuyor) {
                console.warn(`Oturum farklı bir ${denetim.sebep === 'hesap' ? 'hesaba' : 'karta'} ait.`);
                // Oturum BAŞKA BİR HESABA aitse yeniden giriş çözüm değil, güvenlik
                // olayıdır: kullanıcı hesabını başkasına verip onun e-imzasıyla açılan
                // oturum üzerinden işlem yaptırabilir. Engelleyici uyarı gösterilir.
                // Kart uyuşmazlığı ise düzeltilebilir bir durum (doğru kartla yeniden giriş).
                if (denetim.sebep === 'hesap') {
                    ctx.dispatch('kimlikUyusmazligiUyarisiGoster', {sebep: 'hesap'});
                }
                return false;
            }

            return true;
        },
        /**
         * Bağlantı kurulduktan sonraki ilk veri yüklemesi.
         *
         * Üç istek de probe(): burası bağlantı kurma yolunun içi. Buradaki bir 401
         * kurtarmayı tetiklerse kurtarma connectToUyap yayınlar, o da hâlâ çalışmakta
         * olan bu çağrıyı bekler — 48 sn'lik kilit. Bu yolda doğru davranış beklemek
         * değil, hatayı çağırana bildirmek.
         */
        async loadUyapMetaData(ctx: ActionContext<AppState, AppState>): Promise<void> {
            const [bakiyeResult, sorguBakiyeResult, kullaniciResult] = await Promise.allSettled([
                app.$uyap.AvukatBakiyeSorgula().probe().run({}),
                app.$uyap.SorguBakiyesi().probe().run({}),
                app.$uyap.KullaniciBilgileri().probe().run({})
            ]);

            if (bakiyeResult.status === "fulfilled") {
                ctx.commit('setUyapAvukatBakiye', (bakiyeResult.value as { value: string }).value);
            } else {
                ctx.commit('setUyapAvukatBakiye', undefined);
                console.error('AvukatBakiyeSorgula failed', bakiyeResult.reason);
            }

            if (sorguBakiyeResult.status === "fulfilled") {
                const sorguBakiye = (sorguBakiyeResult.value as { sorguBakiye: number }).sorguBakiye;
                ctx.commit('setUyapSorguBakiye', String(sorguBakiye));
            } else {
                ctx.commit('setUyapSorguBakiye', undefined);
                console.error('SorguBakiyesi failed', sorguBakiyeResult.reason);
            }

            if (kullaniciResult.status === "fulfilled") {
                ctx.commit('setUyapAvukatBilgileri', kullaniciResult.value as KullaniciBilgileriCevap);
            } else {
                ctx.commit('setUyapAvukatBilgileri', undefined);
                console.error('KullaniciBilgileri failed', kullaniciResult.reason);
            }

            app.$socket.client.emit("uyapAvukatBilgileri", {
                uyapAvukatBilgileri: ctx.state.uyapAvukatBilgileri,
                uyapAvukatBakiye: ctx.state.uyapAvukatBakiye,
                uyapSorguBakiye: ctx.state.uyapSorguBakiye
            });

            // KullaniciBilgileri oturumun asıl doğrulamasıdır; alınamadıysa oturum
            // geçersizdir ve çağıranlar (connectToUyap, checkUyapSessionNew) bunu bilmeli.
            // Bakiye hataları ise bağlantıyı düşürmez, sessizce geçilir.
            if (kullaniciResult.status === "rejected") {
                const reason = kullaniciResult.reason;
                throw reason instanceof Error ? reason : new Error(String(reason ?? 'UYAP oturumu doğrulanamadı.'));
            }
        },
        async availableCards(ctx: ActionContext<AppState, AppState>, payload?: { silent?: boolean }) {
            console.log("availableCards triggered");
            const silent = Boolean(payload?.silent);
            const oncekiKartVarMi = (ctx.state.availableCardList?.length ?? 0) > 0;
            try{
                let data = await app.$imza.availableCards();
                //console.log("data availableCards",data);
                ctx.commit('setAvailableCardList', data);
                if (Array.isArray(data) && data.length > 0) {
                    const current = ctx.state.selectedCardSerial;
                    // Yeni yapıda kart kimliği card.certificate.certificateId
                    const match = data.find((card: any) => card?.certificate?.certificateId === current);
                    ctx.commit(
                        'setSelectedCardSerial',
                        match ? current : hesabaAitKartiSec(data, sessionStorage.getItem('tckn'))
                    );
                    // 'İ'.toLowerCase() 'i'+U+0307 ürettiği için Türkçe metinlerde
                    // includes eşleşmesi kaçar; tr locale ile küçült.
                    const currentMessage = ctx.state.uyapStatusMessage?.text?.toLocaleLowerCase('tr') ?? '';
                    if (currentMessage.includes('kart') || currentMessage.includes('imzacı') || currentMessage.includes('program')) {
                        ctx.commit('setUyapStatusMessage', null);
                    }
                    // Kart YENİ takıldı (boş → dolu): otomatik girişin tek tetikleyicisi.
                    // "kart takılı" durumu değil, GEÇİŞ tetikliyor; aksi halde her
                    // yoklama yeni bir giriş denemesi olurdu.
                    if (!oncekiKartVarMi) {
                        ctx.dispatch('otomatikGirisDene', {kartGecisi: true})
                            .catch((e) => console.error('Kart takılma akışı:', e));
                    }
                } else {
                    ctx.commit('setSelectedCardSerial', null);
                }
                if (!silent) {
                    //app.$toast.success('Kart listesi alındı');
                }
            }catch (e) {
                console.log("CATCH E",e);
                const errMsg = e instanceof Error ? e.message : String(e);
                ctx.commit('setAvailableCardList', []);
                ctx.commit('setSelectedCardSerial', null);
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: errMsg,
                    timestamp: Date.now()
                });
                if (!silent) {
                    app.$toast.error(errMsg);
                }
            }
        },
        /**
         * Kart yeni takıldı (liste boş → dolu). Otomatik girişin tek tetikleyicisi.
         *
         * Kapılar — hepsi ban ve yanlış-kimlik riskine karşı:
         *  - Devre kesici açıksa hiç denenmez (kullanıcı bilinçli olarak sıfırlamalı).
         *  - Oturum kotası doluysa denenmez; UYAP zaten yeni oturum vermiyor.
         *  - Kart belirsizse (çoklu kart, TCKN eşleşmesi yok) denenmez; seçim kullanıcıda.
         *  - Kart hesaba ait değilse denenmez; aksi halde arka planda tekrar tekrar
         *    "İzinsiz İmza" uyarısı açılırdı.
         *  - PIN kayıtlı değilse denenmez: otomatik akış kullanıcıya PIN modalı açmaz,
         *    kullanıcı "Bağlan"a bastığında zaten sorulur ve bir daha sorulmaz.
         *  - Geri çekilme: art arda başarısızlıkta yeniden deneme aralığı büyür.
         * connectToUyap ayrıca kendi içinde oturumu yokluyor; geçerli oturum varken
         * yeni oturum AÇILMAZ (UYAP'ta en fazla 5 aktif oturum).
         */
        async otomatikGirisDene(
            ctx: ActionContext<AppState, AppState>,
            payload?: { kartGecisi?: boolean }
        ) {
            // Kart yeni takıldı: durum değişti, önceki başarısızlıkların hükmü kalktı.
            if (payload?.kartGecisi) {
                otoGirisBasarisizSayisi = 0;
                otoGirisSonrakiDenemeZamani = 0;
            }
            if (ctx.getters.isUyapConnected) {
                return;
            }
            // Süren bir girişe katılınmaz: o denemenin başarısızlığı her çağıranda
            // ayrı sayılır ve geri çekilme iki katına çıkardı (eİcraPro açılışta
            // updateToken'ı art arda iki kez gönderebiliyor).
            if (ctx.getters.isUyapConnecting) {
                return;
            }
            // eİcraPro kimliği gelmeden denenmez (bkz. connectToUyap); deneme hakkı
            // ve geri çekilme de tüketilmez. Kimlik gelince updateToken yeniden çağırır.
            if (!eicraproBagli(ctx)) {
                console.log('eİcraPro bağlantısı bekleniyor, otomatik UYAP girişi yapılmıyor.');
                return;
            }
            if (devreKesiciAcikMi() || ctx.state.devreKesiciUyarisi) {
                console.warn('Devre kesici açık, otomatik giriş denenmiyor.');
                return;
            }
            // Kota doluyken (5 aktif oturum) her deneme boşuna bir /sign + web1.uyap
            // demek; UYAP yeni oturum vermiyor. Kullanıcı diğer oturumlarını kapatıp
            // dialogdan yeniden denemeli.
            if (ctx.state.oturumLimitiUyarisi) {
                console.warn('UYAP oturum kotası dolu, otomatik giriş denenmiyor.');
                return;
            }
            const serial = ctx.state.selectedCardSerial;
            if (!serial) {
                console.log('Kart seçimi belirsiz, otomatik giriş yapılmıyor.');
                return;
            }
            // Seçili kart hesaba ait değilse otomatik deneme "İzinsiz İmza" uyarısı
            // üretir ve bu uyarı sessiz modda da gösterilir (güvenlik uyarısı).
            // Kullanıcının istemediği bir modalı arka planda tekrar tekrar açmamak
            // için kimlik burada, giriş denenmeden önce kontrol edilir.
            const secili = ctx.state.availableCardList?.find(
                (c: any) => c?.certificate?.certificateId === serial
            );
            const hesapTckn = tcknNormalize(sessionStorage.getItem('tckn'));
            const kartTckn = tcknNormalize(secili?.certificate?.subjectSerial);
            // Bilinmeyen değer uyuşmazlık sayılmaz; eksik veri otomatik girişi
            // engellememeli (tek kart takılıyken TCKN henüz gelmemiş olabilir).
            if (hesapTckn && kartTckn && hesapTckn !== kartTckn) {
                console.warn('Takılı kart hesaba ait değil, otomatik giriş yapılmıyor.');
                // Sessiz çıkmak yetmiyordu: kullanıcı neden bağlanmadığını bilmiyor,
                // ekran yalnızca "Bağlı Değil" gösteriyordu.
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: 'Takılı e-imza kartı bu hesaba ait değil. Kendi hesabınızla ' +
                          'giriş yapın veya size ait e-imza kartını takın.',
                    timestamp: Date.now()
                });
                return;
            }
            if (!app.$imza.kartPinKayitliMi(serial)) {
                console.log('Kart PIN kodu kayıtlı değil, otomatik giriş yapılmıyor.');
                return;
            }
            if (otoGirisBasarisizSayisi >= OTO_GIRIS_MAX_DENEME) {
                console.warn('Otomatik giriş üst üste başarısız oldu, susuluyor. Kartı çıkarıp takın veya "Bağlan"a basın.');
                return;
            }
            if (otoGirisSonrakiDenemeZamani > Date.now()) {
                return;
            }
            try {
                await ctx.dispatch('connectToUyap', {sessiz: true});
                otoGirisBasarisizSayisi = 0;
                otoGirisSonrakiDenemeZamani = 0;
            } catch (e) {
                otoGirisBasarisizSayisi++;
                const bekleme = otoGirisBeklemesiMs(otoGirisBasarisizSayisi);
                otoGirisSonrakiDenemeZamani = Date.now() + bekleme;
                console.error(`Otomatik giriş başarısız (${otoGirisBasarisizSayisi}/${OTO_GIRIS_MAX_DENEME}), ${bekleme / 1000} sn sonra tekrar denenebilir.`, e);
            }
        },
        /**
         * Kart gözlemcisi. Kart takılmasını gören tek yer burası.
         *
         * Yoklama YEREL imzalama uygulamasına gidiyor (127.0.0.1:5975, proxy 6075),
         * UYAP'a değil — bu yüzden sık yoklama ban riski taşımaz. Yine de kart
         * takılıyken sık yoklamanın faydası yok: kart yokken 3 sn, varken 30 sn.
         * Sekme arka plandayken hiç yoklanmaz.
         */
        kartGozlemcisiniBaslat(ctx: ActionContext<AppState, AppState>) {
            if (kartGozlemciTimer !== null) {
                return;
            }
            // document.hidden'a BAKILMAZ: uygulama simge durumuna küçültülmüşken de
            // bot çalışıyor ve kart o sırada takılabiliyor. Yoklama yerel ve ucuz.
            const tik = async () => {
                // Giriş sürerken yoklama yapılmaz: /getCertificates ile /sign aynı
                // token'ı kullanıyor ve sertifika sorgusu kart kullanıldıktan sonra
                // ~4 sn sürüyor; imzalamayı bekletmesin.
                if (ctx.getters.isUyapConnecting) {
                    return;
                }
                const kartVar = (ctx.state.availableCardList?.length ?? 0) > 0;
                const aralik = kartVar ? KART_YOKLAMA_KART_VAR_MS : KART_YOKLAMA_KART_YOK_MS;
                if (Date.now() - sonKartYoklamasi < aralik) {
                    return;
                }
                sonKartYoklamasi = Date.now();
                await ctx.dispatch('availableCards', {silent: true});
            };
            kartGozlemciTimer = window.setInterval(() => {
                tik().catch((e) => console.error('Kart yoklaması başarısız:', e));
            }, KART_YOKLAMA_TIK_MS);
        },
        kartGozlemcisiniDurdur() {
            if (kartGozlemciTimer !== null) {
                clearInterval(kartGozlemciTimer);
                kartGozlemciTimer = null;
                sonKartYoklamasi = 0;
            }
        },
        selectSmartCard(ctx: ActionContext<AppState, AppState>, serial: string | null) {
            ctx.commit('setSelectedCardSerial', serial);
        },
        openUyapWindow() {
            window.open('https://avukatbeta.uyap.gov.tr/', "_blank");
        },
        async fastLoginUyap(ctx: ActionContext<AppState, AppState>) {
            try {
                // probe(): oturum testi, kurtarmaya girmemeli. Bkz. uyapOturumuGecerliMi.
                let avukat = await app.$uyap.KullaniciBilgileri().probe().run({});
                ctx.commit('setUyapConnection', true);
                ctx.commit('setUyapAvukatBilgileri', avukat);
            } catch (e) {
                // Oturum geçersiz: tam e-imza girişine düş.
                await ctx.dispatch('connectToUyap');
                return;
            }
            // Oturum yukarıda doğrulandı; meta verideki geçici bir hata tam
            // e-imza girişine (PIN dialogu) tırmandırılmamalı.
            await ctx.dispatch('loadUyapMetaData').catch((e) => console.error('UYAP meta verisi yüklenemedi:', e));
        },
        async checkUyapSessionNew(ctx: ActionContext<AppState, AppState>) {
            console.log("checkUyapSessionNew triggered");
            try {
                // updateToken ile tetiklenir, yani HESAP DEĞİŞMİŞ olabilir: setConfig
                // sessionStorage'a yeni accessToken ile birlikte yeni tckn de yazar.
                // Oturumun yeni hesaba ait olduğu doğrulanmadan bağlı sayılmamalı,
                // aksi halde bir avukatın UYAP oturumuyla başkasının görevleri işlenir.
                if (!await ctx.dispatch('uyapOturumuGecerliMi')) {
                    ctx.commit('setUyapConnection', false);
                    return;
                }
                await ctx.dispatch('loadUyapMetaData');
                ctx.commit('setUyapConnection', true);
            } catch (error) {
                ctx.commit('setUyapConnection', false);
            }
        },
        async checkUyapSession(ctx: ActionContext<AppState, AppState>) {
            console.log("checkUyapSession triggered");
            // fastLoginUyap geçerli bir UYAP oturumunu kimlik denetimi yapmadan
            // "bağlı" sayıyor; eİcraPro kimliği gelmeden bunu da yapma.
            if (!eicraproBagli(ctx)) {
                return;
            }
            try {
                if (ctx.getters.isEicraproDevMode) {
                    ctx.commit('setUyapConnection', true);
                } else {
                    await app.$imza.setCookieToImza();
                    await ctx.dispatch("fastLoginUyap");
                    ctx.commit('setUyapStatusMessage', {
                        type: 'success',
                        text: 'Uyap oturumu doğrulandı.',
                        timestamp: Date.now()
                    });
                }
            } catch (error) {
                ctx.commit('setUyapConnection', false);
                const message = `Uyap oturumu doğrulanamadı. ${String(error ?? '')}`.trim();
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: message,
                    timestamp: Date.now()
                });
            }
        },
        async setTasksSocket(ctx: ActionContext<AppState, AppState>) {
            // context.di/getTasksFromServer
            await ctx.dispatch("getTasksFromServer")
            app.$socket.client.emit("tasks", ctx.getters.getGorevler);
        },
        async load(ctx: ActionContext<AppState, AppState>): Promise<void> {
            ctx.commit("setAppState", AppStatus.loading);
            // Pencere yenilendiyse kimlik sessionStorage'da durur; yeniden devir beklenmez.
            ctx.commit('tokenDurumunuYenile');
            if (!window.zirvelib) {
                return ctx.commit('setAppState', AppStatus.browser);
            } else {
                const updateRequires = !await ctx.dispatch('isLocalUpToDate');
                console.log("updateRequires", updateRequires);
                if (updateRequires) {
                    return ctx.commit('setAppState', AppStatus.update);
                }
                try {
                    if (!await ctx.dispatch('checkSignService')) {
                        await ctx.dispatch('startSignService');
                    }
                    if (!await ctx.dispatch('checkProxyService')) {
                        await ctx.dispatch('startProxyService');
                    }
                    return ctx.commit('setAppState', AppStatus.electron);
                } catch (e) {
                    console.log(e);
                    return ctx.commit('setAppState', AppStatus.error);
                }
            }
        },
        async isLocalUpToDate(): Promise<boolean> {
            return new Promise((resolve) => {
                if (!window.zirvelib) {
                    return resolve(false);
                }
                if (!window.zirvelib?.version) {
                    return resolve(false);
                }
                console.log("window.zirvelib.version", window.zirvelib.version);
                return resolve(
                    window.zirvelib.version === "2.0.0"
                );
            });
        },

        async checkSignService(): Promise<boolean> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                const heartbeatPromise = axios.post('http://127.0.0.1:4723/xml/heartbeat', {});
                heartbeatPromise.then(() => {
                    console.log('Sign service is running');
                    resolve(true);
                }).catch((error) => {
                    console.warn('Sign service check failed:', error.message);
                    resolve(false);
                });
            })
        },
        async checkProxyService(): Promise<boolean> {
            return new Promise((resolve) => {
                const heartbeatPromise = axios.post('https://imza.eicrapro.com:6090/xml/heartbeat', {});
                heartbeatPromise.then(() => {
                    console.log('Proxy service is running');
                    resolve(true);
                }).catch((error) => {
                    console.warn('Proxy service check failed:', error.message);
                    resolve(false);
                });
            });
        },
        async startSignService(ctx: ActionContext<AppState, AppState>): Promise<void> {
            console.log("Starting Sign Service");
            if (!window.zirvelib) {
                throw new Error('zirvelib not found');
            }
            window.zirvelib.signManager.start();
            // Sabit 2000 ms uyku yerine hazır olmayı yokla: servis 300 ms'de kalkarsa
            // 300 ms beklenir, 3 sn sürerse erken devam edilmez.
            await servisiBekle(() => ctx.dispatch('checkSignService'));
        },

        async startProxyService(ctx: ActionContext<AppState, AppState>): Promise<void> {
            if (!window.zirvelib) {
                throw new Error('zirvelib not found');
            }
            window.zirvelib.proxyManager.start();
            window.zirvelib.socketManager.start();
            await servisiBekle(() => ctx.dispatch('checkProxyService'));
        },

        async stopSignService(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.signManager.kill();
                window.setTimeout(() => {
                    resolve();
                }, 1500)
            })
        },
        async stopProxyService(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!window.zirvelib) {
                    return reject('zirvelib not found');
                }
                window.zirvelib.proxyManager.stop();
                window.zirvelib.socketManager.stop();
                resolve();
            })
        },
        /**
         * İmza servisi (6090 -> 4723) canlılık yoklaması.
         *
         * connectToUyap ÇAĞIRMAZ: Java login yolunda değil (login 6075 + 6081
         * üzerinden), ölü olması oturumun geçersiz olduğu anlamına gelmez.
         * Oturum kurtarma UyapRequest ve GorevYoneticisi içinde zaten var.
         */
        async checkHeartbeat(ctx: ActionContext<AppState, AppState>): Promise<boolean> {
            let canli = false;
            try {
                const response = await fetch('https://imza.eicrapro.com:6090/xml/heartbeat', {
                    method: 'POST',
                    credentials: 'include'
                });
                canli = response.ok;
            } catch (error) {
                console.error('İmza servisi yoklaması başarısız:', error);
            }

            ctx.commit('setSonImzaProbe', {basarili: canli, zaman: Date.now()});

            if (canli) {
                ctx.commit('setHealthCheckRetryCount', 0);
                return true;
            }

            const hata = (ctx.state.healthCheckRetryCount || 0) + 1;
            ctx.commit('setHealthCheckRetryCount', hata);

            // Servisi kendimiz kaldırıyoruz. Eskiden kullanıcıya "ana ekrandan
            // yeniden başlat" deniyordu; o düğme kaldırıldı ve zaten kullanıcının
            // yapacak bir şeyi yoktu — jar sadece imzalama yapıyor, yönetimi bize ait.
            //
            // Yukarıdaki yoklama 6090 (Electron proxy) üzerinden gidiyor; proxy ölü
            // olduğunda da başarısız oluyor. signManager.start() İDEMPOTENT DEĞİL,
            // her çağrıda yeni bir java süreci doğuruyor. Bu yüzden yeniden başlatmadan
            // önce java'nın gerçekten ölü olduğu 127.0.0.1:4723'e doğrudan sorularak
            // doğrulanıyor.
            if (hata === 1 && window.zirvelib) {
                const javaAyakta = await ctx.dispatch('checkSignService').catch(() => false);
                if (!javaAyakta) {
                    console.warn('İmza servisi yanıt vermiyor, yeniden başlatılıyor.');
                    try {
                        await ctx.dispatch('startSignService');
                    } catch (e) {
                        console.error('İmza servisi yeniden başlatılamadı:', e);
                    }
                } else {
                    console.warn('Java imza servisi ayakta; 6090 proxy yanıt vermiyor.');
                }
            }

            if (hata === ctx.state.healthCheckMaxRetries) {
                ctx.commit('setUyapStatusMessage', {
                    type: 'error',
                    text: 'İmza servisi yanıt vermiyor ve yeniden başlatılamadı. ' +
                          'Doküman imzalama çalışmayabilir; uygulamayı kapatıp açmayı deneyin.',
                    timestamp: Date.now()
                });
                console.error('İmza servisi yoklaması üst üste başarısız');
            }
            return false;
        },
        startHealthCheck(ctx: ActionContext<AppState, AppState>) {
            // Zaten çalışıyorsa tekrar başlatma
            if (ctx.state.connectionHealthCheckInterval !== null) {
                console.log('Health check zaten çalışıyor');
                return;
            }

            console.log('Health check başlatıldı');

            // Ardışık başarısızlıkta geri çekilme: 30 -> 60 -> 120 sn.
            const TABAN_MS = HEALTH_CHECK_ARALIK_MS;
            const TAVAN_MS = 120000;

            const interval = setInterval(async () => {
                // Kaçan updateToken'a karşı: ekran (Bağlan düğmesi, görev listesi)
                // en geç bir tikte gerçek kimlik durumuna döner.
                ctx.commit('tokenDurumunuYenile');
                const hata = ctx.state.healthCheckRetryCount || 0;

                // Bağlıyken normal izleme; bağlantı reconnect denemesi sırasında
                // düştüyse retry penceresi boyunca denemeye devam et — aksi halde
                // ilk başarısız denemeden sonra otomatik kurtarma tamamen durur
                // ve max-retry mesajına hiç ulaşılamaz.
                const retrying = hata > 0 && hata < ctx.state.healthCheckMaxRetries;
                if (ctx.getters.isUyapConnected || retrying) {
                    // Tik sabit olduğu için geri çekilme tik atlayarak uygulanır.
                    // YALNIZCA bu dala uygulanır: geri çekilme imza servisi
                    // yoklamasına ait, oturum gözcüsünün kendi geri çekilmesi var
                    // ve ikisi birbirini yavaşlatmamalı.
                    if (hata > 0) {
                        const beklenenMs = Math.min(TABAN_MS * Math.pow(2, hata), TAVAN_MS);
                        const atlanacakTik = Math.round(beklenenMs / TABAN_MS);
                        healthTikSayaci++;
                        if (healthTikSayaci % atlanacakTik !== 0) {
                            return;
                        }
                    }
                    await ctx.dispatch('checkHeartbeat');
                    return;
                }

                // Oturum gözcüsü: kart takılı ama oturum yok. Kart takılma olayı
                // (boş → dolu geçişi) bu durumu kapsamıyor — kart yerinde dururken
                // oturum düşmüş olabilir. otomatikGirisDene kendi kapılarını
                // (devre kesici, PIN, kart belirsizliği, geri çekilme) uyguluyor.
                await ctx.dispatch('otomatikGirisDene');
            }, TABAN_MS);

            ctx.commit('setHealthCheckInterval', interval);
        },
        stopHealthCheck(ctx: ActionContext<AppState, AppState>) {
            if (ctx.state.connectionHealthCheckInterval !== null) {
                clearInterval(ctx.state.connectionHealthCheckInterval);
                ctx.commit('setHealthCheckInterval', null);
                ctx.commit('setHealthCheckRetryCount', 0);
                healthTikSayaci = 0;
                console.log('Health check durduruldu');
            }
        },
    }
})

// Kota dolduğunda uyarıyı göster. Kanca burada kaydediliyor ki BaglantiKurtarma
// store/DOM'a bağlanmasın ve uyap-plugin'i kullanan diğer uygulamalarda da
// sorunsuz çalışsın.
kotaUyarisiKancasiniAyarla(() => {
    store.dispatch('oturumLimitiUyarisiGoster');
});

// 401 devre kesici açıldığında: bot durur, engelleyici dialog açılır.
devreKesiciKancasiniAyarla(() => {
    store.dispatch('devreKesiciUyarisiGoster');
});

// Açık oturum bu hesaba ait değil: UyapRequest isteği hiç göndermeden fırlatır,
// burada bot durdurulup engelleyici dialog açılır.
kimlikUyusmazligiKancasiniAyarla((sebep) => {
    store.dispatch('kimlikUyusmazligiUyarisiGoster', {sebep});
});

// Kurtarma sırasındaki durum mesajı. Bu uygulamada UyapStatusCard'da satır içi
// gösteriliyor; icrapro-app aynı kancayı toast'a bağlıyor.
durumMesajiKancasiniAyarla((tip: DurumMesajiTipi, metin: string) => {
    store.commit('setUyapStatusMessage', {
        type: tip,
        text: metin,
        timestamp: Date.now()
    });
});
